/* eslint-disable @typescript-eslint/no-explicit-any */

import { PartialHighlight } from '../../typings/store';

/**
 * Configuration for the HighlightEngine scheduler.
 *
 * Controls queue size, timing, and pacing (idle gaps, repeat cooldowns, etc.).
 *
 * Units:
 * - All durations in this type are **milliseconds**.
 * - Incoming `Highlight.duration` (from the server) is interpreted as **seconds** and
 *   converted to milliseconds internally. If it’s missing/≤0, `defaultDurationMs` is used.
 */
export type EngineOptions = {
  /**
   * Maximum number of items to keep in the internal queue.
   * Extra items beyond this cap are dropped (most-recent-first insertion).
   *
   * @default 100
   */
  maxKeep?: number;

  /**
   * Fallback duration **in ms** used when an item has no valid `duration` from the server.
   * (Server `duration` is expected in **seconds**; the engine multiplies by 1000.)
   *
   * @default 5000
   */
  defaultDurationMs?: number;

  /**
   * How often (in ms) the engine performs a deep sweep to:
   * - prune expired items (`expirationAt` <= now),
   * - clean auxiliary maps,
   * - and generally keep the queue tidy.
   *
   * @default 10000
   */
  sweepEveryMs?: number;

  /**
   * Minimum idle time (in ms) after we become highlight-less **and the queue is empty**
   * before we’re allowed to show a new highlight. Prevents “instant pop-in” when items
   * trickle in after an idle period. Also used when an enforced idle is triggered by
   * `maxConsecutive`.
   *
   * @default 800
   */
  minIdleGapMs?: number;

  /**
   * Maximum number of highlights that may play back-to-back without an idle gap.
   * Once this number is reached, the engine enforces an idle pause of `minIdleGapMs`
   * even if more items are queued.
   *
   * Use `Infinity` (the default) to disable this behavior.
   *
   * @default Infinity
   */
  maxConsecutive?: number;

  /**
   * Cooldown (in ms) before the **same highlight id** is eligible to play again
   * when it is re-enqueued via `repeat` (which is a numeric counter).
   * After a highlight finishes and has `repeat > 0`, it’s re-added with `repeat - 1`
   * and becomes ineligible until `now + repeatIntervalMs`.
   *
   * @default 2000
   */
  repeatIntervalMs?: number;
};

export type EngineState = {
  current: PartialHighlight | null;
  queue: PartialHighlight[];
};

type Listener = (s: EngineState) => void;

const toMillis = (x?: string | number | null) => {
  if (x == null) return undefined;
  if (typeof x === 'number') return x;
  const n = Date.parse(x);
  return Number.isNaN(n) ? undefined : n;
};

const isExpired = (h: PartialHighlight, now = Date.now()) => {
  const exp = toMillis(h.expirationAt as any);
  return exp !== undefined && exp <= now;
};

const normalize = (h: any): PartialHighlight => ({
  id: String(h.id),
  title: h.title ?? '',
  duration: Number.isFinite(h.duration) ? h.duration : undefined, // seconds (server)
  priority: Number.isFinite(h.priority) ? h.priority : undefined,
  repeat: Number.isFinite(h.repeat) ? (h.repeat as number) : h.repeat ? 1 : 0,
  ...h,
});

const upsertById = <T extends { id?: string }>(arr: T[], item: T): T[] => {
  if (!item.id) return arr;
  const i = arr.findIndex((x) => x.id === item.id);
  if (i === -1) return [item, ...arr];
  const clone = arr.slice();
  clone[i] = { ...clone[i], ...item };
  return clone;
};

// priority desc, createdAt asc
const pickNext = (list: PartialHighlight[], now: number, eligibleAt: Map<string, number>) => {
  const active = list.filter((h) => !isExpired(h, now) && now >= (eligibleAt.get(h.id!) ?? 0));
  if (active.length === 0) return null;
  return active
    .slice()
    .sort(
      (a, b) =>
        (b.priority ?? 0) - (a.priority ?? 0) ||
        (toMillis(a.createdAt as any) ?? 0) - (toMillis(b.createdAt as any) ?? 0),
    )[0];
};

export class HighlightEngine {
  // opts
  private maxKeep: number;
  private defaultDurationMs: number;
  private sweepEveryMs: number;
  private minIdleGapMs: number;
  private maxConsecutive: number;
  private repeatIntervalMs: number;

  // state
  private current: PartialHighlight | null = null;
  private queue: PartialHighlight[] = [];

  // timing
  private rafId: number | null = null;
  private currentEndAt: number | null = null;
  private nextSweepAt: number;
  private idleUntil: number | null = null;
  private consecutive = 0;
  private eligibleAt = new Map<string, number>(); // id -> ms

  // listeners
  private listeners = new Set<Listener>();

  constructor(opts: EngineOptions = {}) {
    this.maxKeep = opts.maxKeep ?? 100;
    this.defaultDurationMs = opts.defaultDurationMs ?? 5000;
    this.sweepEveryMs = opts.sweepEveryMs ?? 10_000;
    this.minIdleGapMs = opts.minIdleGapMs ?? 800;
    this.maxConsecutive = opts.maxConsecutive ?? Number.POSITIVE_INFINITY;
    this.repeatIntervalMs = opts.repeatIntervalMs ?? 2000;

    this.nextSweepAt = Date.now() + this.sweepEveryMs;
  }

  /** Subscribe to state updates. Returns unsubscribe. */
  subscribe(fn: Listener) {
    this.listeners.add(fn);
    // immediate notify
    fn(this.getState());
    return () => this.listeners.delete(fn);
  }

  /** Get a snapshot (read-only clones). */
  getState(): EngineState {
    return { current: this.current, queue: this.queue.slice(0) };
  }

  /** Update options on the fly. */
  setOptions(opts: Partial<EngineOptions>) {
    if (opts.maxKeep != null) this.maxKeep = opts.maxKeep;
    if (opts.defaultDurationMs != null) this.defaultDurationMs = opts.defaultDurationMs;
    if (opts.sweepEveryMs != null) this.sweepEveryMs = opts.sweepEveryMs;
    if (opts.minIdleGapMs != null) this.minIdleGapMs = opts.minIdleGapMs;
    if (opts.maxConsecutive != null) this.maxConsecutive = opts.maxConsecutive;
    if (opts.repeatIntervalMs != null) this.repeatIntervalMs = opts.repeatIntervalMs;
  }

  /** Start the rAF loop (idempotent). */
  start() {
    if (this.rafId != null) return;
    const step = () => {
      const now = Date.now();
      let changed = false;

      // prune queue
      this.queue = this.queue.filter((x) => !isExpired(x, now));

      // current: expiration / duration
      if (this.current) {
        if (isExpired(this.current, now)) {
          const willBeEmpty = this.queue.length === 0;
          this.current = null;
          this.currentEndAt = null;
          if (willBeEmpty) {
            this.idleUntil = now + this.minIdleGapMs;
            this.consecutive = 0;
          } else if (this.consecutive >= this.maxConsecutive) {
            this.idleUntil = now + this.minIdleGapMs;
            this.consecutive = 0;
          }
          changed = true;
        } else if (this.currentEndAt != null && now >= this.currentEndAt) {
          const repeatCount = typeof this.current.repeat === 'number' ? this.current.repeat : 0;
          if (repeatCount > 0) {
            // re-enqueue with repeat-1 and cooldown
            this.queue = this.queue.filter((x) => x.id !== this.current!.id);
            this.queue.push({
              ...this.current!,
              repeat: repeatCount - 1,
              createdAt: new Date().toISOString(),
            });
            this.eligibleAt.set(this.current!.id!, now + this.repeatIntervalMs);
          }
          const willBeEmpty = this.queue.length === 0;
          this.current = null;
          this.currentEndAt = null;
          if (willBeEmpty) {
            this.idleUntil = now + this.minIdleGapMs;
            this.consecutive = 0;
          } else if (this.consecutive >= this.maxConsecutive) {
            this.idleUntil = now + this.minIdleGapMs;
            this.consecutive = 0;
          }
          changed = true;
        }
      }

      // idle → maybe start next (respect idleUntil)
      if (!this.current) {
        if (this.queue.length === 0 && this.idleUntil == null) {
          this.idleUntil = now + this.minIdleGapMs;
          this.consecutive = 0;
        }

        const gated = this.idleUntil != null && now < this.idleUntil;
        if (!gated) {
          const next = pickNext(this.queue, now, this.eligibleAt);
          if (next) {
            this.queue = this.queue.filter((x) => x.id !== next.id);
            const durationMs =
              typeof next.duration === 'number' && next.duration > 0
                ? next.duration * 1000
                : this.defaultDurationMs;

            this.currentEndAt = now + (durationMs ?? this.defaultDurationMs);
            this.current = next;
            this.idleUntil = null;
            this.consecutive = (this.consecutive ?? 0) + 1;
            changed = true;
          }
        }
      }

      // deep sweep + cleanup eligibility map + cap
      if (now >= this.nextSweepAt) {
        const alive = new Set<string>(this.queue.map((q) => q.id));
        if (this.current) alive.add(this.current.id!);
        for (const k of Array.from(this.eligibleAt.keys())) {
          if (!alive.has(k)) this.eligibleAt.delete(k);
        }
        this.queue = this.queue.filter((x) => !isExpired(x, now));
        this.nextSweepAt = now + this.sweepEveryMs;
        changed = true;
      }

      if (this.queue.length > this.maxKeep) {
        this.queue = this.queue.slice(0, this.maxKeep);
        changed = true;
      }

      if (changed) this.emit();

      this.rafId = window.requestAnimationFrame(step);
    };

    this.rafId = window.requestAnimationFrame(step);
  }

  /** Stop the rAF loop (keeps state as-is). */
  stop() {
    if (this.rafId != null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Clear queue & current. */
  clear() {
    this.queue = [];
    this.current = null;
    this.currentEndAt = null;
    this.idleUntil = null;
    this.consecutive = 0;
    this.eligibleAt.clear();
    this.emit();
  }

  /** Enqueue a new highlight (raw or normalized). */
  enqueue(raw: PartialHighlight | any) {
    const h = (raw as any).id ? normalize(raw) : raw; // tolerate already-normalized
    h.repeat = 0; // force no-repeat for enqueued highlights
    if (!this.eligibleAt.has(h.id!)) this.eligibleAt.set(h.id!, 0);
    this.queue = upsertById(this.queue, h);
    this.emit();
  }

  /** Batch enqueue. */
  enqueueMany(items: any[]) {
    for (const it of items) this.enqueue(it);
  }

  private emit() {
    const snapshot = this.getState();
    this.listeners.forEach((fn) => fn(snapshot));
  }
}
